import { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { addToast } from "@/store/app/action.js";
import { getComplianceStates } from "@/api/taxRules.js";
import PurchaseAttestationModal from "./PurchaseAttestationModal.jsx";
import { US_STATES } from "@/utils/usStates.js";

function PurchaseAttestations() {
  const dispatch = useDispatch();
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalState, setModalState] = useState(null);

  const loadStates = async () => {
    try {
      setLoading(true);
      const data = await getComplianceStates();
      setStates(data || []);
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || "Failed to load purchase attestations",
        type: "error",
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStates();
  }, []);

  const handleRowClick = (state) => {
    setModalState(state);
    setModalOpen(true);
  };

  const attestationStates = useMemo(() => {
    if (typeFilter === "all") return states;
    return states.filter((s) => s.legal_disclaimer_type === typeFilter);
  }, [states, typeFilter]);

  return (
    <div>
      {/* Type filter */}
      <div className="mb-4">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="form-select w-full md:w-64"
        >
          <option value="all">All Types</option>
          <option value="DISPLAY">Display</option>
          <option value="ACTION">Action</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="table-auto w-full dark:text-slate-300">
          {loading ? (
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              <tr>
                <td colSpan="3" className="px-2 first:pl-5 last:pr-5 py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 dark:text-slate-400">Loading purchase attestations...</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <>
              <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">State</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Type</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3">
                    <div className="font-semibold text-left">Disclaimer</div>
                  </th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                {attestationStates.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-2 first:pl-5 last:pr-5 py-8 text-center text-slate-500">
                      No purchase attestations configured.
                    </td>
                  </tr>
                ) : (
                  attestationStates.map((state) => {
                    const stateName = US_STATES.find((s) => s.code === state.code)?.name || state.code;
                    return (
                      <tr
                        key={state.code}
                        onClick={() => handleRowClick(state)}
                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
                      >
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap align-top">
                          <div className="font-medium text-slate-800 dark:text-slate-100">
                            {stateName} ({state.code})
                          </div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap align-top">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                            state.legal_disclaimer_type === "ACTION"
                              ? "bg-amber-100 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400"
                              : "bg-sky-100 text-sky-600 dark:bg-sky-400/10 dark:text-sky-400"
                          }`}>
                            {state.legal_disclaimer_type || "—"}
                          </span>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 align-top">
                          <div className="text-left whitespace-pre-wrap text-slate-600 dark:text-slate-300">
                            {state.legal_disclaimer || "—"}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </>
          )}
        </table>
      </div>

      <PurchaseAttestationModal
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        state={modalState}
        stateName={modalState ? (US_STATES.find((s) => s.code === modalState.code)?.name || modalState.code) : ""}
        onSave={loadStates}
      />
    </div>
  );
}

export default PurchaseAttestations;
